import { get, off, onValue, ref } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { LaunchError } from "../utils/errors";
import { postAgencyLinkingEndpoint } from "../services/agencyAccountLinking";
import {
  agencyLinkBatchPath,
  applyAppSumoAgencyMode,
  applyPromptLinkStatusToAccounts,
  buildLinkRequest,
  emptyAgencyLinkingPrompt,
  excludeLinkedAgencyAccounts,
  extractAdAccountsFromIntegration,
  filterAccountsByAvailablePlatforms,
  normalizeAgencyLinkBatch,
  normalizeAgencyLinkingPrompt,
  normalizeLinkResponse,
  AGENCY_LINKING_VARIANT,
} from "../utils/agencyAccountLinking";
import {
  getAgencyLinkingAvailablePlatforms,
  isAgencyAccountLinkingEnabledNow,
  isAgencyLinkingAvailabilityReady,
  peekPendingAgencyLink,
  PICKER_NOT_SHOWN,
  requestNewConnectionPicker,
  setAgencyLinkingAvailability,
} from "../agencyAccountLinking/session";

const ENDPOINT = Object.freeze({
  LINK: "linkAdAccountsToAgency",
  PROMPT: "getAgencyLinkingPrompt",
});

let inFlightPrompt = null;

const PROMPT_POST_CONNECT_RETRY_MS = [0, 500, 1200, 2500];

const waitMs = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const shouldRetryAgencyPromptAfterConnect = ({
  prompt,
  integrationAccounts,
}) => {
  if (prompt?.consentBlocked || integrationAccounts.length === 0) {
    return false;
  }
  if (prompt?.showPickerLine === true) {
    return false;
  }
  if (prompt?.variant === AGENCY_LINKING_VARIANT.DIRECT) {
    return false;
  }
  return (
    prompt?.variant == null || prompt?.variant === AGENCY_LINKING_VARIANT.AGENCY
  );
};

const loadPromptAfterPlatformConnect = async (
  dispatch,
  { platform, integration }
) => {
  let prompt = emptyAgencyLinkingPrompt();
  for (const delayMs of PROMPT_POST_CONNECT_RETRY_MS) {
    if (delayMs > 0) {
      await waitMs(delayMs);
    }
    prompt = await dispatch(startGetAgencyLinkingPrompt({ force: true }));
    const integrationAccounts = filterAccountsByAvailablePlatforms({
      accounts: extractAdAccountsFromIntegration({ platform, integration }),
      availablePlatforms: prompt.availablePlatforms,
    });
    if (!shouldRetryAgencyPromptAfterConnect({ prompt, integrationAccounts })) {
      break;
    }
  }
  return prompt;
};

const postLinking = async ({ dispatch, endpoint, data }) => {
  const token = await dispatch(renewToken());
  return postAgencyLinkingEndpoint({ endpoint, token, data });
};

// agencies/{agencyID}/isAppSumo forces the agency sheet. A failed read stays
// on the prompt's own variant instead of guessing.
const readAgencyIsAppSumo = async (dispatch) => {
  try {
    const { agencyID } = dispatch(getPathDatabaseByRole());
    if (!agencyID) return false;
    const snapshot = await get(ref(db, `agencies/${agencyID}/isAppSumo`));
    return snapshot.val() === true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

const loadAgencyLinkingPrompt = async (dispatch) => {
  try {
    const [raw, isAppSumo] = await Promise.all([
      postLinking({
        dispatch,
        endpoint: ENDPOINT.PROMPT,
        data: {},
      }),
      readAgencyIsAppSumo(dispatch),
    ]);
    const prompt = applyAppSumoAgencyMode({
      prompt: normalizeAgencyLinkingPrompt(raw),
      isAppSumo,
    });
    setAgencyLinkingAvailability({
      availablePlatforms: prompt.availablePlatforms,
      consentBlocked: prompt.consentBlocked,
      showPickerLine: prompt.showPickerLine,
      ready: true,
    });
    return prompt;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: ENDPOINT.PROMPT,
      })
    );
    return emptyAgencyLinkingPrompt();
  }
};

export const startGetAgencyLinkingPrompt =
  ({ force = false } = {}) =>
  (dispatch) => {
    if (!force && inFlightPrompt) return inFlightPrompt;
    inFlightPrompt = loadAgencyLinkingPrompt(dispatch).finally(() => {
      inFlightPrompt = null;
    });
    return inFlightPrompt;
  };

export const startLinkAdAccountsToAgency =
  ({ source, locale, accounts = [], resumeBatchID }) =>
  async (dispatch) => {
    if (!isAgencyLinkingAvailabilityReady()) {
      await dispatch(startGetAgencyLinkingPrompt());
    }
    const data = buildLinkRequest({
      source,
      locale,
      accounts,
      resumeBatchID,
      availablePlatforms: getAgencyLinkingAvailablePlatforms(),
    });
    if (data.accounts.length === 0) {
      return { ...normalizeLinkResponse({ ok: false }), skipped: true };
    }
    try {
      const raw = await postLinking({
        dispatch,
        endpoint: ENDPOINT.LINK,
        data,
      });
      return normalizeLinkResponse(raw);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: ENDPOINT.LINK,
        })
      );
      return normalizeLinkResponse({ ok: false });
    }
  };

// Never throws: the caller's own post-connection flow must always continue.
export const startOpenNewConnectionPicker =
  ({ platform, integration }) =>
  async (dispatch) => {
    try {
      if (!isAgencyAccountLinkingEnabledNow()) return PICKER_NOT_SHOWN;
      if (peekPendingAgencyLink()) return PICKER_NOT_SHOWN;
      const prompt = await loadPromptAfterPlatformConnect(dispatch, {
        platform,
        integration,
      });
      if (prompt.consentBlocked || prompt.showPickerLine !== true) {
        return PICKER_NOT_SHOWN;
      }
      const integrationAccounts = filterAccountsByAvailablePlatforms({
        accounts: extractAdAccountsFromIntegration({ platform, integration }),
        availablePlatforms: prompt.availablePlatforms,
      });
      const enriched = applyPromptLinkStatusToAccounts({
        accounts: integrationAccounts,
        promptTargets: prompt.targetAccounts || [],
      });
      const accounts = excludeLinkedAgencyAccounts(enriched);
      return requestNewConnectionPicker({
        platform,
        integration,
        accounts,
      });
    } catch (error) {
      console.log(error);
      return PICKER_NOT_SHOWN;
    }
  };

/**
 * Shows the new-connection picker when applicable and resolves after it is
 * closed, so the caller's original post-connection flow always runs.
 */
export const startAfterPlatformConnected =
  ({ platform, integration }) =>
  async (dispatch) => {
    const picker = await dispatch(
      startOpenNewConnectionPicker({ platform, integration })
    );
    await picker.closed;
  };

const listenAgencyLinkNode = ({ path, onUpdatedDataCallback, mapValue }) => {
  const dbRef = ref(db, path);
  onValue(
    dbRef,
    (snapshot) => {
      onUpdatedDataCallback(mapValue(snapshot.val()));
    },
    (error) => {
      console.log(error);
    }
  );
  return dbRef;
};

// Toast counters for one click. The node is `agencyLinkBatches/{batchID}`.
export const startListenerAgencyLinkBatch =
  ({
    batchID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async () => {
    try {
      if (!batchID) return false;
      const listenerRef = listenAgencyLinkNode({
        path: agencyLinkBatchPath({ batchID }),
        onUpdatedDataCallback,
        mapValue: normalizeAgencyLinkBatch,
      });
      onReferenceAvailable(listenerRef);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

export const stopListenerAgencyLinkBatch =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };
